import React from "react";
import { Formik } from "formik";
import { CForm, CRow, CCol, CButton, CSpinner } from "@coreui/react";
import { useNavigate } from "react-router-dom";
import CustomInput from "@/components/CustomInput";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { dummyUser } from "@/constants/translation/dummyUser.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { dummyUser as dummyUserRoutes } from "@/libs/routes";
import constants from "@/libs/constant";

/**
 * DummyUserForm Component
 * Shared add/edit form for Dummy Users.
 *
 * Props:
 * - initialValues: { first_name, last_name, email, house_number, street, city, country, zip_code, latitude, longitude }
 * - onSubmit: (values) => void
 * - isSubmitting: boolean
 * - validationSchema: Yup schema
 * - isEdit: boolean - hides the read-only password field and shows an "Update" button
 */
const DummyUserForm = ({ initialValues, onSubmit, isSubmitting, validationSchema, isEdit = false }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, handleChange, handleBlur, setFieldValue, errors, touched }) => (
        <CForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <CRow className="mb-2 m-auto p-3 border rounded">
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.first_name_label)}
                type={constants.inputTypes.text}
                name="first_name"
                placeholder={t(dummyUser.form.first_name_placeholder)}
                value={values.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.first_name && errors.first_name}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.last_name_label)}
                type={constants.inputTypes.text}
                name="last_name"
                placeholder={t(dummyUser.form.last_name_placeholder)}
                value={values.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.last_name && errors.last_name}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.email_label)}
                type={constants.inputTypes.text}
                name="email"
                placeholder={t(dummyUser.form.email_placeholder)}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && errors.email}
              />
            </CCol>
            {!isEdit && (
              <CCol lg={6} md={6} sm={12}>
                <CustomInput
                  label={t(dummyUser.form.password_label)}
                  type={constants.inputTypes.text}
                  name="password"
                  value={constants.dummyUser.defaultPassword}
                  disabled
                  onChange={() => {}}
                />
              </CCol>
            )}
            <CCol lg={12} md={12} sm={12}>
              <GoogleAddressInput
                onPlaceSelected={(parsed) => {
                  setFieldValue("house_number", parsed.house_number);
                  setFieldValue("street", parsed.street);
                  setFieldValue("city", parsed.city);
                  setFieldValue("country", parsed.country);
                  setFieldValue("zip_code", parsed.zip_code);
                  setFieldValue("latitude", parsed.latitude);
                  setFieldValue("longitude", parsed.longitude);
                }}
                error={
                  (touched.latitude || touched.longitude) && (errors.latitude || errors.longitude)
                    ? t(dummyUser.form.coordinates_missing_note)
                    : undefined
                }
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.house_number_label)}
                type={constants.inputTypes.text}
                name="house_number"
                placeholder={t(dummyUser.form.house_number_placeholder)}
                value={values.house_number}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.house_number && errors.house_number}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.street_label)}
                type={constants.inputTypes.text}
                name="street"
                placeholder={t(dummyUser.form.street_placeholder)}
                value={values.street}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.street && errors.street}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.city_label)}
                type={constants.inputTypes.text}
                name="city"
                placeholder={t(dummyUser.form.city_placeholder)}
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.city && errors.city}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.country_label)}
                type={constants.inputTypes.text}
                name="country"
                placeholder={t(dummyUser.form.country_placeholder)}
                value={values.country}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.country && errors.country}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(dummyUser.form.zip_code_label)}
                type={constants.inputTypes.text}
                name="zip_code"
                placeholder={t(dummyUser.form.zip_code_placeholder)}
                value={values.zip_code}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.zip_code && errors.zip_code}
              />
            </CCol>
          </CRow>

          <CRow className="m-auto p-3">
            <CCol className="d-flex gap-2 justify-content-center">
              <CButton type="submit" color="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    {t(dummyUser.form.loading_btn_text)}
                  </>
                ) : isEdit ? (
                  t(dummyUser.form.update_btn_text)
                ) : (
                  t(dummyUser.form.submit_btn_text)
                )}
              </CButton>
              <CButton
                type="button"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigate(dummyUserRoutes.list)}
              >
                {t(dummyUser.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>
        </CForm>
      )}
    </Formik>
  );
};

export default DummyUserForm;
